// Thread-level "Connect <provider>" prompt. When an agent needs a
// required-connection remote MCP (e.g. honeycomb) the user can't use yet, the
// backend emits a `connect_required` signal that nativeThreadMessages turns
// into data parts on the assistant message. This surfaces them once the run has
// FINISHED (so they read as a follow-up, not a mid-stream interruption), and
// connects via a popup so the chat stays open. Provider-agnostic.
//
// The message signal is authoritative (see pendingProviders): it comes from the
// runtime's live MCP check, which also catches a stored token the upstream
// rejects — the status endpoint still says "connected" there. A provider drops
// out once the user connects it from this message, advancing through multiple
// required providers. Connect times are kept in per-user localStorage so a
// remount or reload doesn't re-prompt for a provider just connected.

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuiState } from '@assistant-ui/react';
import { useCapabilities } from '@/app/providers/CapabilitiesProvider';
import { toast } from '@/shared/ui/sonner';
import { getOAuthProvider } from '../model/providerRegistry';
import { pendingProviders, readConnectedAt, recordConnected, requiredProviders } from '../model/connectRequired';
import { useConnectProvider } from '../model/useConnectProvider';

export function ConnectProviderBanner() {
  const messages = useAuiState((state) => state.thread.messages);
  const isRunning = useAuiState((state) => state.thread.isRunning);
  const userId = useCapabilities().capabilities?.user_id;
  const [localConnectedAt, setLocalConnectedAt] = useState<Record<string, number>>({});
  const connectedAt = { ...readConnectedAt(userId), ...localConnectedAt };

  const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant');
  const providers = pendingProviders(lastAssistant?.createdAt, requiredProviders(lastAssistant), connectedAt);

  // Only surface once the answer has finished streaming.
  if (isRunning || providers.length === 0) return null;
  const provider = providers[0];
  return (
    <ConnectPrompt
      key={provider}
      provider={provider}
      onConnected={() => setLocalConnectedAt(recordConnected(userId, provider))}
    />
  );
}

function ConnectPrompt({ provider, onConnected }: { provider: string; onConnected: () => void }) {
  const queryClient = useQueryClient();
  const label = getOAuthProvider(provider)?.label ?? provider;

  const { connect, isPending } = useConnectProvider(provider, {
    onConnected: () => {
      toast.success(`${label} connected. Your next message can use it.`, { duration: 6000 });
      // Keep other status consumers (e.g. the plugins page) fresh.
      void queryClient.invalidateQueries({ queryKey: ['integration-status', provider] });
      onConnected();
    },
  });

  return (
    <div className="mx-auto my-2 flex w-full max-w-[70rem] flex-col gap-2 rounded-lg border border-primary/40 bg-card p-3">
      <p className="text-xs font-semibold text-muted-foreground">{label} isn&apos;t connected</p>
      <p className="text-sm text-muted-foreground">
        Connect {label} to pull the data this answer needs. A sign-in window opens; your chat stays open.
      </p>
      <button
        type="button"
        disabled={isPending}
        className="w-fit rounded-md border border-border px-3 py-1 text-sm transition-colors hover:bg-accent disabled:opacity-60"
        onClick={() => connect()}
      >
        {isPending ? 'Connecting…' : `Connect ${label}`}
      </button>
    </div>
  );
}
